import { useCallback, useContext } from 'react';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { mimeTypeToPcpType } from '@wix/da-shared-react/pkg/utils/mime';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { FileUploadOptions } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { pushUnlockVideoUploadsModal } from '../../redux/actions/modal';

export interface ReturnType extends Pick<FileUploadOptions, 'validator' | 'onDropRejected'> {
  showModal: () => void;
  areVideoUploadsLocked: boolean;
}

export const LOCKED_VIDEO_UPLOAD_FILEERROR = {
  message: 'locked video upload',
  code: '',
};

/** Returns:
 *  - a dialog telling the user to upgrade to core
 *  - and a function that will show that dialog if any of its given files
 *  being uploaded are the video type (to be called in useFileUpload's onDrop)
 */
export function useUnlockVideoUploadsCheck(): ReturnType {
  const { hasCoreMembership } = useContext(SessionContext);
  const areVideoUploadsLocked = !hasCoreMembership;

  const dispatchPushUnlockVideoUploadsModal = useAction(pushUnlockVideoUploadsModal);

  return {
    /** function to show the upsell modal directly */
    showModal: dispatchPushUnlockVideoUploadsModal,
    /** whether video uploads are locked for the current user */
    areVideoUploadsLocked,
    // note: the following two can be combined/chained with other validators
    // and rejection handlers
    validator: useCallback(
      file => {
        const isVideoMime = mimeTypeToPcpType(file.type) === 'video';
        return areVideoUploadsLocked && isVideoMime ? LOCKED_VIDEO_UPLOAD_FILEERROR : null;
      },
      [areVideoUploadsLocked]
    ),
    onDropRejected: useCallback(
      rejected => {
        const hasRejectedVideo = rejected.some(rejection => {
          return rejection.errors.includes(LOCKED_VIDEO_UPLOAD_FILEERROR);
        });
        hasRejectedVideo && dispatchPushUnlockVideoUploadsModal();
      },
      [dispatchPushUnlockVideoUploadsModal]
    ),
  };
}
